import React, { useEffect, useRef, useState } from "react";
import { HeaderPage } from "../base/headerPage";
import { GetSystemConfigData, getUserPrefConfigInfo, updateUserPrefConfigInfo } from "../../lib/common-service";
import { getUserCredential } from "../../lib/cookies";
import PlanCompFilter from "./filter";
import { LoadingPanel } from "src/components/loadingPanel";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { providerPlanStatementSelector } from "src/redux/selectors/custom-selector";
import useProviderPlanStatementService from "./service";
import ProviderPlanSummaryHeader from "./ProviderPlanSummaryHeader";
import ProviderKPIPaymentSummary from "./ProviderKPIPaymentSummary";
import ProviderSectionTwo from "./ProviderSectionTwo";
import ExcelExportProviderSectionOne from "./ExcelExportProviderSectionOne";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { renderToString } from "react-dom/server";
import PDFPageTemplate from "./PDFPageTemplate";
import { saveAs } from "@progress/kendo-file-saver";
import AdvanceQueryFilter from "../advanceQueryFilter/advanceQueryFilter";
import { DialogComponent } from "src/components/dialog";

import { getReportScreenHeight } from "src/utils/utils";
import {
  setShowReportResult,
  setProviderShowSection2,
  setProviderShowSection1,
  setProviderAdvanceFilterSelectionsAction,
  setProviderAdvanceFilterDropdownsAction,
  setProviderAdvanceSavedFilterAction,
  setProviderAdvanceFilterTooltipAction,
} from "src/redux/actions/providerplan-actions";

const ProviderPlanStatementReport = (pageProps) => {
  const [isReportRunning, setIsRunningReport] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDirectApplyLoading, setIsDirectApplyLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [savedAppliedFilter, setSavedAppliedFilter] = useState(null);
  const [appliedFilterTooltip, setAppliedFilterTooltip] = useState("");
  const [showDiscardAdvFilterConfirm, setShowDiscardAdvFilterConfirm] = useState(false);
  const [advFilterResetSignal, setAdvFilterResetSignal] = useState(0);
  const [shouldRunReport, setShouldRunReport] = useState(false);
  const cookiesData = getUserCredential();
  const locationPath = pageProps?.location?.pathname?.slice(1) || "";

  const exportSec1Ref = useRef(null);
  const exportSec2Ref = useRef(null);
  const section1ExportRef = useRef(null);
  const section2Ref = useRef(null);

  const {
    selectedCompPlan,
    selectedCompPlanPeriod,
    isProviderDetail,
    providerPlanSummaryHeader,
    providerKpiPaymentSummary,
    showSection1,
    showSection2,
    showReportResults,
    providerSection2Data,
    advanceFilterSelections,
    // advance filter tooltip form stay in sync (they'd otherwise reset on remount).
    advanceSavedFilter,
    advanceFilterTooltip,
  } = useSelector(providerPlanStatementSelector, shallowEqual);

  // Derived — no separate useState needed; persists across navigation via redux.
  const isAdvanceFilterApplied = advanceFilterSelections !== null;

  const reportService = useProviderPlanStatementService();

  const [reportHeight, setReportHeight] = useState(0);

  const [systemReportPeriodInfo, setSystemReportPeriodInfo] = useState({});
  const [userPrefConfigInfo, setUserPrefConfigInfo] = useState({});
  const [userPrefColumnsList, setUserPrefColumnsList] = useState("");
  const [isPeriodDataLoaded, setIsPeriodDataLoaded] = useState(false);

  useEffect(() => {
    getSystemConfigInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSystemConfigInfo = async () => {
    let dataObject = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: "AND system_pref_code = 'sys_report_periods'",
      in_sort_number: null,
      in_sort_order: null,
      in_page_row_offset: null,
      in_page_rows: null,
    };
    try {
      const resDataObject = await GetSystemConfigData(dataObject, null, dispatch);
      if (resDataObject && resDataObject?.length > 0) {
        setSystemReportPeriodInfo(resDataObject[0]);

        await Promise.all([
          getUserPrefConfig(resDataObject[0]?.system_pref_code),
          getUserPrefConfigForColumns("report_period_col_pref"),
        ]);
      }
    } catch (error) {
      console.error("Error fetching user preference configurations:", error);
    } finally {
      setIsPeriodDataLoaded(true);
    }
  };

  const getUserPrefConfig = async (in_user_pref_code) => {
    let dataObject = {
      in_user_pref_code,
      db_obj_code: locationPath || "",
    };
    const resDataObject = await getUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject && resDataObject?.length > 0) {
      setUserPrefConfigInfo(resDataObject[0]);
    }
  };

  const getUserPrefConfigForColumns = async (in_user_pref_code) => {
    let dataObject = {
      in_user_pref_code,
      db_obj_code: locationPath || "",
    };
    const resDataObject = await getUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject && resDataObject?.length > 0) {
      if (resDataObject[0]?.user_pref_value) {
        setUserPrefColumnsList(resDataObject[0]?.user_pref_value);
      }
    }
  };

  const handleUpdateUserPrefConfigInfo = async (values) => {
    if (userPrefConfigInfo && systemReportPeriodInfo && !systemReportPeriodInfo?.system_pref_code) return;

    let dataObject = {
      in_user_pref_code: systemReportPeriodInfo?.system_pref_code,
      db_obj_code: locationPath || "",
      user_pref_value: values,
    };
    const resDataObject = await updateUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject) {
      setUserPrefConfigInfo({ ...userPrefConfigInfo, user_pref_value: values });
    }
  };

  const handleUpdateUserPrefColumnsList = async (values) => {
    let dataObject = {
      in_user_pref_code: "report_period_col_pref",
      db_obj_code: locationPath || "",
      user_pref_value: values,
    };
    const resDataObject = await updateUserPrefConfigInfo(dataObject, null, dispatch);
    if (resDataObject) {
      setUserPrefColumnsList(values);
    }
  };

  useEffect(() => {
    if (isReady) {
      if (showReportResults) {
        setReportHeight(getReportScreenHeight() - 20);
      } else {
        setReportHeight(0);
      }
    }
  }, [showReportResults, isReady]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (shouldRunReport) {
      setShouldRunReport(false);
      runReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRunReport]);

  // "Select All" means the cascaded dropdown's actual keys, not unfiltered (0).
  // Show query result must not expand from Redux dropdowns — those can still hold
  // a previous unconstrained provider list. Resolve from the cascade APIs so
  // Search and Show query result send the same keys.
  const applyAdvanceFilter = async ({ filterObj, providerDetail }) => {
    const normalizeSelectAll = (id) => (id === "Select All" ? "0" : id);
    const getIds = (field) =>
      (filterObj?.filters || [])
        .filter((f) => f?.field === field)
        .flatMap((f) => (Array.isArray(f.value) ? f.value : [f.value]))
        .filter(Boolean)
        .map((v) => normalizeSelectAll(String(v).replaceAll('"', "").trim()));

    const compPlanIds = getIds("comp_plan_key");
    const periodIds = getIds("period_key");
    const specialtyIds = getIds("specialty_key");
    const groupIds = getIds("title_key");
    const providerIds = getIds("provider_key");

    const isConstrained = (ids) => ids.length > 0 && !ids.includes("0");
    const keysFromList = (list, keyField) =>
      (list || []).filter((it) => Number(it[keyField]) !== 0).map((it) => String(it[keyField]));

    const specialtyConstrained = isConstrained(specialtyIds);
    const groupConstrained = isConstrained(groupIds);
    let effectiveGroupIds = groupIds;
    let effectiveProviderIds = providerIds;

    const needsGroupExpand = specialtyConstrained && !groupConstrained;
    const needsProviderExpand =
      (specialtyConstrained || groupConstrained || needsGroupExpand) && !isConstrained(providerIds);

    if (needsGroupExpand || needsProviderExpand) {
      setIsDirectApplyLoading(true);
      try {
        const cascadeContext = {
          comp_plan_key: compPlanIds[0],
          period_key: periodIds[0],
          specialty_key: specialtyConstrained ? specialtyIds : undefined,
          title_key: groupConstrained ? groupIds : undefined,
        };
        if (needsGroupExpand) {
          const groupResult = await reportService.fetchAdvFilterGroups(cascadeContext);
          const expanded = keysFromList(groupResult?.data, "title_key");
          if (expanded.length) {
            effectiveGroupIds = expanded;
            cascadeContext.title_key = expanded;
          }
        }
        if (needsProviderExpand) {
          if (!cascadeContext.title_key && isConstrained(effectiveGroupIds)) {
            cascadeContext.title_key = effectiveGroupIds;
          }
          const providerResult = await reportService.fetchAdvFilterProviders(cascadeContext);
          const expanded = keysFromList(providerResult?.data, "provider_key");
          if (expanded.length) {
            effectiveProviderIds = expanded;
          }
        }
      } finally {
        setIsDirectApplyLoading(false);
      }
    }

    dispatch(
      setProviderAdvanceFilterSelectionsAction({
        selectedCompPlan: compPlanIds.length ? { comp_plan_key: Number(compPlanIds[0]) } : null,
        selectedCompPlanPeriod: periodIds.length ? { period_key: Number(periodIds[0]) } : null,
        selectedSpecialities: specialtyIds.map((id) => ({ specialty_key: id === "0" ? 0 : Number(id) })),
        selectedGroups: effectiveGroupIds.map((id) => ({ title_key: id === "0" ? 0 : Number(id) })),
        selectedProviders: effectiveProviderIds.map((id) => ({ provider_key: id === "0" ? 0 : Number(id) })),
        isProviderDetail: Boolean(providerDetail),
      })
    );

    // The outer filter bar is deliberately NOT touched — its Provider Detail
    // toggle keeps whatever the user set there. Only the advance selections above
    // drive the report (via effectiveIsProviderDetail in service.js), so the two
    // filters stay independent, same as the Plan Comp Statement report.

    setShouldRunReport(true);
  };

  const runReports = async () => {
    setIsRunningReport(true);
    //dispatch(setPrefetchDropdownAction(false));
    dispatch(setShowReportResult(false));
    dispatch(setProviderShowSection2(true));
    try {
      await reportService.getAutoHideSection1();
      await reportService.fetchSummaryHeaderProvider();
      await reportService.fetchProviderKpiPaymentSummary({
        in_filter: null,
        in_sort_number: 2,
        in_sort_order: "ASC",
      });
      await reportService.fetchReportSummaryColumnHeaders();
      await reportService.fetchProviderKpiPaymentDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setIsRunningReport(false);
    }
  };

  const handleExport = async () => {
    const option = {
      paperSize: "A4",
      forcePageBreak: ".page-break",
      avoidLinks: true,
      landscape: true,
      margin: { top: "2cm", left: "0.5cm", right: "0.5cm", bottom: "1.5cm" },
      scale: 0.5,
      repeatHeaders: true,
      template: (props) => {
        let newProps = { ...props };
        newProps.compPlan = selectedCompPlan.comp_plan_name;
        newProps.period = selectedCompPlanPeriod.period_name;
        let z = document.createElement("div");
        z.innerHTML = renderToString(PDFPageTemplate(newProps));
        return z;
      },
    };
    try {
      setIsExporting(true);
      setTimeout(async () => {
        let group1;
        let group2;
        if (showSection1) {
          group1 = await drawDOM(exportSec1Ref.current, option);
        }
        if (showSection2) {
          await new Promise((r) => setTimeout(r, 1000));
          group2 = await drawDOM(exportSec2Ref.current, {
            ...option,
            scale: 0.46,
          });
        }

        if (group1 && group2) {
          group2.children.forEach((it) => {
            group1.children.push(it);
          });
          exportPDF(group1, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "provider_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        } else if (group1) {
          exportPDF(group1, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "provider_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        } else if (group2) {
          exportPDF(group2, { multiPage: true })
            .then((uri) => {
              saveAs(uri, "provider_comp_statement.pdf");
              setIsExporting(false);
            })
            .catch(() => {
              setIsExporting(false);
            });
        }
      }, 2000);
    } catch (error) {}
  };

  const handleExcelExport = () => {
    const sheets = [];
    const fileName = "Provider_Comp_Statement.xlsx";

    if (showSection1) {
      const section1Workbook = section1ExportRef.current?.getWorkbookOptions();
      if (section1Workbook?.sheets?.length) {
        sheets.push(...section1Workbook.sheets);
      }
    }

    if (showSection2) {
      const section2Workbook = section2Ref.current?.getWorkbookOptions();
      if (section2Workbook?.sheets?.length) {
        sheets.push(...section2Workbook.sheets);
      }
    }

    if (!sheets.length) {
      return;
    }

    const saver = section2Ref.current || section1ExportRef.current;
    saver?.saveWorkbook({ sheets }, fileName);
  };

  const canExportSection1 =
    showSection1 && (providerPlanSummaryHeader.data?.length > 0 || providerKpiPaymentSummary.data?.length > 0);
  const canExportSection2 = showSection2 && providerSection2Data?.data?.length > 0;

  const actionMenu = {
    reportSection1: {
      checked: showSection1,
      label: "Section 1",
      onChange: (checked) => {
        dispatch(setProviderShowSection1(checked));
      },
    },
    reportSection2: {
      checked: showSection2,
      label: "Section 2",
      onChange: (checked) => {
        dispatch(setProviderShowSection2(checked));
      },
    },
    exportToPDF: {
      caption: "Export to PDF",
      disabled: !showSection1 && !showSection2,
      fx: handleExport,
    },
    export: {
      caption: "Export to Excel",
      disabled: !canExportSection1 && !canExportSection2,
      fx: handleExcelExport,
    },
  };

  const headerConfig = {
    title: "Provider Comp Statement",
    id: "provider_comp_statement_report",
    action_menu: showReportResults ? actionMenu : undefined,
    // headerPage reads the filter-icon tooltip off pageConfig, not props.
    appliedFilterQuery: advanceFilterTooltip,
    isQueryFilterApplied: isAdvanceFilterApplied,
    search_fx: (e) => {
      e?.preventDefault?.();
      setShowAdvanceSearch(true);
    },
  };

  useEffect(() => {
    let timer = setTimeout(() => setIsReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady || !isPeriodDataLoaded) {
    return <LoadingPanel />;
  }

  return (
    <div className="Main">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} isQueryFilterApplied={isAdvanceFilterApplied} />
        </div>
        <div className="content-body">
          <ExcelExportProviderSectionOne ref={section1ExportRef} />
          <AdvanceQueryFilter
            open={showAdvanceSearch}
            resetSignal={advFilterResetSignal}
            onClose={() => setShowAdvanceSearch(false)}
            config={{
              dbObjName: locationPath,
              module: "provider_comp_report",
              showDefaultExpression: true,
              defaultExpressionField: "period_key",
              requiredFields: [
                { field: "comp_plan_key", label: "Comp Plan" },
                { field: "period_key", label: "Comp Plan Period" },
              ],
            }}
            initialToggleValues={{ is_provider_detail: isProviderDetail }}
            savedAppliedFilter={advanceSavedFilter}
            onDirectApplyLoading={(loading) => setIsDirectApplyLoading(loading)}
            onApply={({
              filterObj,
              filterData,
              mainLogic,
              formFields,
              gridHistoryId,
              appliedFilterTooltip: tooltip,
              appliedFilterQuery,
              toggleValues,
            }) => {
              dispatch(
                setProviderAdvanceSavedFilterAction({
                  filterData,
                  mainLogic,
                  formFields,
                  gridHistoryId: gridHistoryId || "",
                  appliedFilterQuery: appliedFilterQuery || "",
                  toggleValues: toggleValues || {},
                })
              );
              dispatch(setProviderAdvanceFilterTooltipAction(tooltip || ""));
              applyAdvanceFilter({ filterObj, providerDetail: toggleValues?.is_provider_detail });
            }}
            onSavedFilterUpdate={({
              filterData,
              mainLogic,
              formFields,
              gridHistoryId,
              appliedFilterQuery,
              toggleValues,
            }) => {
              dispatch(
                setProviderAdvanceSavedFilterAction({
                  filterData,
                  mainLogic,
                  formFields,
                  gridHistoryId: gridHistoryId || "",
                  appliedFilterQuery: appliedFilterQuery || "",
                  toggleValues: toggleValues || {},
                })
              );
            }}
          />
          <div id="topSection">
            <PlanCompFilter
              runReports={() => {
                // Running from the outer bar discards any active advance filter —
                // confirm first, as the Plan Comp Statement report does. Changing a
                // filter-bar dropdown does NOT discard it; only RUN does (via this popup).
                if (isAdvanceFilterApplied) {
                  setShowDiscardAdvFilterConfirm(true);
                } else {
                  runReports();
                }
              }}
            />
          </div>
          <div
            className="provider-reports-comp-detail-section-scrollable-height"
            style={{
              overflowY: showSection1 ? "scroll" : "scroll",
              maxHeight: reportHeight,
              overflowX: "hidden",
              marginTop: 20,
            }}
          >
            <div>
              <div ref={exportSec1Ref}>
                {showReportResults && showSection1 && providerPlanSummaryHeader.columns.length ? (
                  <ProviderPlanSummaryHeader isExporting={isExporting} />
                ) : null}
                {showReportResults && showSection1 && providerKpiPaymentSummary.columns.length ? (
                  <div className="page-break">
                    <ProviderKPIPaymentSummary isReportRunning={isReportRunning} isExporting={isExporting} />
                  </div>
                ) : null}
              </div>
              {showReportResults && showSection2 ? (
                <div ref={exportSec2Ref} className={showSection1 ? "page-break" : ""}>
                  <ProviderSectionTwo
                    ref={section2Ref}
                    isReportRunning={isReportRunning}
                    isExporting={isExporting}
                    reportHeight={reportHeight}
                    systemReportPeriodInfo={systemReportPeriodInfo}
                    userPrefConfigInfo={userPrefConfigInfo}
                    updateUserPrefConfigInfo={handleUpdateUserPrefConfigInfo}
                    userPrefColumnsList={userPrefColumnsList}
                    onUpdateUserPrefColumnsList={handleUpdateUserPrefColumnsList}
                    isPeriodDataLoaded={isPeriodDataLoaded}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {isReportRunning || isExporting || isDirectApplyLoading ? <LoadingPanel /> : null}
          {showDiscardAdvFilterConfirm && (
            <DialogComponent
              title="Discard Advance Filter?"
              desc="An advance filter is currently applied. Do you want to discard it and run the report using the outer filter values?"
              visible={showDiscardAdvFilterConfirm}
              onCancelConfirm={() => setShowDiscardAdvFilterConfirm(false)}
              actionBtn={[
                {
                  name: "No",
                  onClick: () => setShowDiscardAdvFilterConfirm(false),
                },
                {
                  name: "Yes",
                  onClick: () => {
                    setShowDiscardAdvFilterConfirm(false);
                    dispatch(setProviderAdvanceFilterSelectionsAction(null));
                    dispatch(setProviderAdvanceSavedFilterAction(null));
                    dispatch(setProviderAdvanceFilterTooltipAction(""));
                    dispatch(
                      setProviderAdvanceFilterDropdownsAction({
                        compPlan: [],
                        period: [],
                        speciality: [],
                        group: [],
                        provider: [],
                      })
                    );
                    setAdvFilterResetSignal((s) => s + 1);
                    setShouldRunReport(true);
                  },
                },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderPlanStatementReport;
